import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedFormItemId, setGridSelection, reorderElementReference, transferElementReference, pushFormActionStack, reorderUndoElementReference, transferUndoRedoElementReference, removeElement, undoDeleteElement } from '../../../../../../stateManager/store/formCreation_v2/actions'
import { ReactComponent as FolderOpenIcon } from "../../../../../../assets/svg/solobuilder/folderopen.svg";
import { ReactComponent as FolderExpandIcon } from "../../../../../../assets/svg/solobuilder/folderexpand.svg";
import { ReactComponent as AssetOpenIcon } from "../../../../../../assets/svg/solobuilder/assetopen.svg";
import { ReactComponent as AssetExpandIcon } from "../../../../../../assets/svg/solobuilder/assetexpand.svg";
import {ReactComponent as DeleteIcon} from "../../../../../../assets/images/form_icons/deleteIcon.svg"
import { containers } from "../../constants";
import { useDrag, useDrop } from "react-dnd";
import { ItemTypes } from "../../types/ItemTypes";
import { myRedoStack, myUndoStack } from "../../stack/Stack";
import { LeafNode } from './LeafNode'
import { getContainerField } from "../../helpers";
import { setCondElVisibility } from "../../helpers";
import { MouseHoverOnTree } from "../../../../../../helpers/RxServices";
import {ReactComponent as CloseEye} from "../../../../../../assets/svg/closeEye_icon.svg"
import { getComponents, getHighlightedText } from "../../helpers/assetHelpers";
import { FormCreationData } from "../../../../../../stateManager/store/formCreation_v2/types";
import classNames from "classnames";
import "../panel.css"

export const ParentNode = (props: any) => {
  const dispatch = useDispatch()
  const { isCoreV3 } = useSelector((state: any) => state.formJson);
  const selectedElementId = props.selectedElementId
  const { visibility, handleToggleVisibility, parentHoveredElId} = props
  const { id: itemId = "", type = "", isAsset, isChildAsset } = props.item
  const isTopLevel = props.item.hasOwnProperty('componentOrder')
  const isChildrenVisibile = isTopLevel ? visibility.canvas : visibility.components[itemId] !== false
  const [hoveredElId, setHoveredElId] = useState<string | null>(null)
  const fields = isChildrenVisibile ? isTopLevel ? props.item.componentOrder : props.item.fields : []
  const deviceType = useSelector((state: any) => state.formJson.deviceType)
  const textRef = useRef<HTMLParagraphElement>(null);
  const [textWidth, setTextWidth] = useState(0);
  const [isNearEdgeTop, setIsNearEdgeTop] = useState<boolean>(false)
  const nodeRef = useRef<any>(null)
  const newStyles: { [key: string]: any } = {
    ...props.item?.properties?.styles?.common,
    ...props.item?.properties?.styles?.[deviceType],
  }; 
  const { components, componentOrder } = getComponents()
  const [labelValue, setLabelValue] = useState<any>();
  const [{ isDragging }, drag] = useDrag({
    item: { type: ItemTypes.TREE_NODE, json: { ...props.item, index: props.index } },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !isTopLevel && props.isCheckedIn,
  });

  const [{ isOverCurrent }, drop] = useDrop({
    accept: [
      ItemTypes.TREE_NODE,
    ],
    canDrop: (_item: any) => {
      if (isAsset || isChildAsset){
        return false
      }
      return true
    },
    collect: (monitor) => {
      return {
        isOverCurrent: monitor.isOver({ shallow: true }),
      };
    },
    hover: (_item: any, monitor) => {
      if (isOverCurrent && nodeRef.current) {
        const clientOffset = monitor.getClientOffset();
        const hoverBoundingRect = nodeRef.current?.getBoundingClientRect();
        if (!clientOffset || !hoverBoundingRect) {
          return;
        }
        const hoverheight = hoverBoundingRect.bottom - hoverBoundingRect.top;
        const mouseY = clientOffset.y;
        const relativeMouseY = mouseY - hoverBoundingRect.top;
        const edgeThresold = 0.3;
        const isNearTopEdge = relativeMouseY < hoverheight * edgeThresold;
        if (isNearTopEdge !== isNearEdgeTop) {
          setIsNearEdgeTop(isNearTopEdge);
        }
      }
    },
    drop: (item: any) => {
      if (isOverCurrent) {
        const { json } = item
        const { id, parentId } = props.item
        const dropTarget = {
          index: props.index,
          id,
          parentId
        }
        const dragSource = {
          id: json.id,
          index: json.index,
          parentId: json.parentId,
        }
        
        // Prevent drop element into an asset
        if(components[dropTarget?.id]?.isAsset || components[dropTarget?.id]?.isChildAsset){
          return;
        }

        // Prevent drop element is not a tab inside tabbar
        const isTabbar = components[dragSource?.parentId]?.type === "TabBar";
        if (isTabbar && item.json.type !== 'Tab') {
          return;
        }

        const isTab = components[dropTarget?.parentId]?.type === "Tab";
        if (isTab && item.json.type === 'Tab') {
          return;
        }
        const isNotTab = components[dropTarget?.id]?.type === "TabBar";
        if (isNotTab && item.json.type !== 'Tab') {
          return;
        }

        // If parent gets dropped on its own child, ignore action
        if (dragSource.id === dropTarget.parentId) {
          return
        }

        // If item is dropped into its own parent, ignore action
        if (dropTarget.id === dragSource.parentId) {
          // Prevent dropping inside the same container unless it's near the top edge (reordering above)
          if (!isNearEdgeTop) {
            return
          } else {
            //  Attempting to drop into another container when the item is near the top edge
            const payload: any = {
              id: json.id,
              dragSourceId: dragSource.parentId,
              dropTargetId: dropTarget.parentId,
              index: dropTarget.index
            }
            dispatch(transferElementReference({
              id: json.id,
              dragSourceId: dragSource.parentId,
              dropTargetId: dropTarget.parentId,
              index: dropTarget.index
            }))
            // Undo-Redo
            myUndoStack.push({ ...payload });
            dispatch(pushFormActionStack({
              undo: () => {
                const poppedElement = myUndoStack.pop();
                if (poppedElement) {
                  myRedoStack.push(poppedElement);
                  dispatch(transferUndoRedoElementReference({
                    id: poppedElement.id,
                    dragSourceId: poppedElement.dropTargetId,
                    dropTargetId: poppedElement.dragSourceId,
                    index: dragSource.index,
                    increment: -1
                  }))
                }
              },
              redo: () => {
                const poppedElement = myRedoStack.pop();
                if (poppedElement) {
                  myUndoStack.push({ ...poppedElement });
                  dispatch(transferUndoRedoElementReference({
                    id: poppedElement.id,
                    dragSourceId: poppedElement.dragSourceId,
                    dropTargetId: poppedElement.dropTargetId,
                    index: poppedElement.index,
                    increment: 1
                  }))
                }
              }
            }));
            return
          }
        }

        // If dragged item and dropped item are the same, ignore action
        if (dragSource.id === dropTarget.id) {
          return
        }

        // If drag and drop locations are on the same parent, reorder them
        if (dropTarget.parentId === dragSource.parentId) {
          // if the mouse is not near the top edgee , attempt to move the item to the drop target container
          if (!isNearEdgeTop) {
            const payload: any = {
              id: json.id,
              dragSourceId: dragSource.parentId,
              dropTargetId: dropTarget.id,
              index: 0
            }
            dispatch(transferElementReference({
              id: json.id,
              dragSourceId: dragSource.parentId,
              dropTargetId: dropTarget.id,
              // Dropping into container title should place the element on first index
              index: 0
            }))
            // Undo-Redo
            myUndoStack.push({ ...payload });
            dispatch(pushFormActionStack({
              undo: () => {
                const poppedElement = myUndoStack.pop();
                if (poppedElement) {
                  myRedoStack.push(poppedElement);
                  dispatch(transferUndoRedoElementReference({
                    id: poppedElement.id,
                    dragSourceId: poppedElement.dropTargetId,
                    dropTargetId: poppedElement.dragSourceId,
                    index: dragSource.index,
                    increment: -1
                  }))
                }
              },
              redo: () => {
                const poppedElement = myRedoStack.pop();
                if (poppedElement) {
                  myUndoStack.push({ ...poppedElement });
                  dispatch(transferUndoRedoElementReference({
                    id: poppedElement.id,
                    dragSourceId: poppedElement.dragSourceId,
                    dropTargetId: poppedElement.dropTargetId,
                    index: poppedElement.index,
                    increment: 1
                  }))
                }
              }
            }));
  
          } 
          // otherwise reorder within the container 
          else {
            dispatch(reorderElementReference({
            dropTarget,
              dragSource,
            }))
            // undoredo
            const payload: any = { dropTarget, dragSource }
            myUndoStack.push({ ...payload });
            dispatch(pushFormActionStack({
              undo: () => {
                const poppedElement = myUndoStack.pop();
                if (poppedElement) {
                  myRedoStack.push(poppedElement);
                  dispatch(reorderUndoElementReference({
                    dropTarget: {
                      index: poppedElement.dragSource.index,
                      id: "",
                      parentId: poppedElement.dragSource.parentId,
                    },
                    dragSource: {
                      ...poppedElement.dragSource,
                      index: poppedElement.dropTarget.index,
                      parentId: poppedElement.dropTarget.parentId,
                    },
                    increment: -1
                  }))
                }
              },
              redo: () => {
                const poppedElement = myRedoStack.pop();
                if (poppedElement) {
                  myUndoStack.push({ ...poppedElement });
                  dispatch(reorderUndoElementReference({
                    dropTarget: poppedElement.dropTarget,
                    dragSource: poppedElement.dragSource,
                    increment: 1
                  }))
                }
              }
            }));
          }
        }

        // If drag and drop locations are from different parents, transfer it into dropped container
        if (dropTarget.parentId !== dragSource.parentId) {
          const payload: any = {
            id: json.id,
            dragSourceId: dragSource.parentId,
            // If dropping near the top edge, treat the parent as the drop target to insert above; otherwise, drop into the current target.
            dropTargetId: isNearEdgeTop ? dropTarget.parentId : dropTarget.id,
            index: dropTarget.index
          }
          dispatch(transferElementReference({
            id: json.id,
            dragSourceId: dragSource.parentId,
            // If dropping near the top edge, treat the parent as the drop target to insert above; otherwise, drop into the current target.
            dropTargetId: isNearEdgeTop ? dropTarget.parentId : dropTarget.id,
            index: dropTarget.index
          }))
          // Undo-Redo
          myUndoStack.push({ ...payload });
          dispatch(pushFormActionStack({
            undo: () => {
              const poppedElement = myUndoStack.pop();
              if (poppedElement) {
                myRedoStack.push(poppedElement);
                dispatch(transferUndoRedoElementReference({
                  id: poppedElement.id,
                  dragSourceId: poppedElement.dropTargetId,
                  dropTargetId: poppedElement.dragSourceId,
                  index: dragSource.index,
                  increment: -1
                }))
              }
            },
            redo: () => {
              const poppedElement = myRedoStack.pop();
              if (poppedElement) {
                myUndoStack.push({ ...poppedElement });
                dispatch(transferUndoRedoElementReference({
                  id: poppedElement.id,
                  dragSourceId: poppedElement.dragSourceId,
                  dropTargetId: poppedElement.dropTargetId,
                  index: poppedElement.index,
                  increment: 1
                }))
              }
            }
          }));
        }
      }
    },
  });
  useEffect(() => {
    const subscription = MouseHoverOnTree.getMessage().subscribe((e) => {
      setHoveredElId(e);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const filterNodes = (node: any) => {
    let searchValue = props?.searchValue
    let searchBy = node?.type?.toLowerCase() + '-' + node?.id;
    const { json, item } = props;

    if (!searchValue) {
      return true;
    }

    const { component } = getContainerField(node?.id, item) || {};

    if (searchBy.toLowerCase().includes(searchValue.toLowerCase())) {
      return true;
    }

    // Check if the node's parent ID matches
    const matchesParentId = (parentId: string): boolean => {
      const { component } = getContainerField(parentId, item) || {};
      searchBy = component?.type?.toLowerCase() + '-' + parentId;
      if (searchBy.toLowerCase().includes(searchValue.toLowerCase())) {
        return true;
      }
      
      const parentNode = json.components[parentId];
      
      if (parentNode && parentNode.parentId) {
        return matchesParentId(parentNode.parentId);
      }
      return false;
    };

    if (component?.parentId && matchesParentId(component.parentId)) {
      return true;
    }

    if (component?.fields?.length > 0) {
      for (const child of component.fields) {
        const childNode = json.components[child];
        if (filterNodes(childNode)) {
          return true;
        }
      }
    }
    return false;
  };

  // Filter fields based on the search query
  const filteredFields = fields.filter((field: any) => {
    const node = props.json.components[field];
    return filterNodes(node);
  });

  // to expand the tree node while searching
  useEffect(() => {
    handleToggleVisibility(true, itemId, "open")
  }, [props.searchValue])

  const modifiedLabel = () => {
    let label = !isTopLevel ? type.toLowerCase() + '-' + itemId : 'Canvas';
    if (props.searchValue && !isTopLevel) {
      return getHighlightedText(label, props.searchValue)
    } else { 
      return label
    }
  }

  useEffect(() => {
    const label = modifiedLabel();
    setLabelValue(label);
  }, [props?.searchValue]);

const handleMouseEnter = ( itemId: string) => {
  setHoveredElId(itemId); 
  setCondElVisibility(itemId, props.json.components);
  MouseHoverOnTree.sendMessage(itemId);
};

const handleMouseLeave = () => {
  parentHoveredElId ? MouseHoverOnTree.sendMessage(parentHoveredElId) : MouseHoverOnTree.sendMessage(null)
};
  function getNestedComponents(fields: string[], array: FormCreationData[] = []) {
    fields.forEach((fieldId: string) => {
      const component = components[fieldId];
      array.push(component);
      // recursively all fields
      if (component?.fields?.length) {
        getNestedComponents(component.fields, array);
      }
    })
  }
  const handleDelete = async (e: any, component: any) => {
    e.stopPropagation();
    if (hoveredElId) {
      const childrenArray: FormCreationData[] = [];
      getNestedComponents(component?.fields, childrenArray);
      const payload: FormCreationData = {
        ...component,
        id: components[hoveredElId].id,
        parentId: components[hoveredElId].parentId || "",
        type: components[hoveredElId].type,
        children: childrenArray,
      }

      if (components[hoveredElId].parentId) {
        const parentId: any = components[hoveredElId].parentId;
        payload.metadata = {
          position: components[parentId].fields.indexOf(components[hoveredElId].id)
        }
      }

      // Undo Redo for delete elements
      myUndoStack.push({ ...payload });
      // component order
      myUndoStack.saveOrder(componentOrder);
      const order = myUndoStack.getOrder();

      dispatch(pushFormActionStack({
        undo: () => {
          const poppedElement = myUndoStack.pop();
          if (poppedElement) {
            myRedoStack.push(poppedElement);
            dispatch(
              undoDeleteElement(poppedElement, order),
            )
          }
        },
        redo: () => {
          const poppedElement = myRedoStack.pop();
          if (poppedElement) {
            myUndoStack.push({ ...poppedElement });
            dispatch(removeElement({
              id: poppedElement.id,
              parentId: poppedElement.parentId || "",
              type: poppedElement.type,
              increment: 1
            }))
          }
        }
      }));

      dispatch(removeElement({
        id: components[hoveredElId].id,
        parentId: components[hoveredElId].parentId,
        type: components[hoveredElId].type
      }))
    }
  }

  const childWidth = useRef<HTMLDivElement | null>(null);
  const dynamicWidth = childWidth.current ? (Math.min(props?.scrollInfo.containerWidth + props?.scrollInfo.scrollLeft, props?.scrollInfo.scrollWidth) - childWidth.current.offsetLeft) - 30 : 0;
  useEffect(() => {
    if (textRef.current) {
      setTextWidth(textRef.current.scrollWidth);
    }
  }, [labelValue]);

  const handleItemClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCondElVisibility(itemId, props.json.components);
    if (isTopLevel) {
      dispatch(setGridSelection({ isGridSelected: true }));
    } else {
      dispatch(setSelectedFormItemId({
        id: itemId,
        isAssetInstanceSelected: isAsset || isChildAsset,
      }));
    }
  };

  return (
    <>
      {isCoreV3 ?
        <div
          key={itemId}
          ref={(ref) => {
            isTopLevel ? drop(ref) : drag(drop(ref));
            nodeRef.current = ref;
          }}
          className={classNames(
            { "opacity-40": isDragging, "opacity-100": !isDragging }, 
            { "bg-highlight_onhover": hoveredElId === itemId }, 
            { 
              "tree-drop-leaf": isOverCurrent && isNearEdgeTop,
              "tree-drop-parent": isOverCurrent && !isNearEdgeTop
            }
          )}
          style={{width: `${props?.parentWidth}px`}}
          onMouseEnter={() => handleMouseEnter(itemId)}
          onMouseLeave={handleMouseLeave}
        >
          <div className={`cursor-pointer flex items-center justify-between ${hoveredElId === itemId && "bg-highlight_onhover"} ${!isTopLevel ? 'py-1' : ''} ${(!isTopLevel && selectedElementId === itemId && props.isCheckedIn ? 'text-hdb-blue' : '')}`}
            style={{ minWidth: textWidth + 62 }}
          >
            <div className="flex items-center gap-2 py-1 w-full overflow-hidden" title={labelValue}>
            <div onClick={() => handleToggleVisibility(isTopLevel, itemId)}>
                {isChildrenVisibile ? isAsset ? <AssetOpenIcon /> : <FolderOpenIcon />
                  : isAsset ? <AssetExpandIcon /> : <FolderExpandIcon />}
            </div>
              <div
                className={classNames('truncateText', {
                  truncate: !newStyles.visible || hoveredElId === itemId,
                })}
                ref={textRef}
                style={{
                  minWidth: "40px",
                  maxWidth: !newStyles.visible || hoveredElId === itemId ? `${dynamicWidth}px` : "unset",
                }}
                onClick={handleItemClick} title={labelValue}>
                {labelValue}
              </div>
            </div>
            {
              ((!newStyles.visible && dynamicWidth > 38) || (newStyles.visible && dynamicWidth > 18)) &&
              <div className="flex sticky right-0">
                  {!newStyles.visible && !isTopLevel  && <div className={`mr-2`}><CloseEye width={13} height={13} title="You've hidden this widget"/></div>}
                {labelValue != "Canvas" && hoveredElId === itemId && !isChildAsset && <div className={`mr-2`}><DeleteIcon onClick={(e) => handleDelete(e, props?.item)} width={13} height={13} title="Delete Widget"/></div>}
              </div>
            }
          </div>
          <div ref={childWidth} className="ml-8 max-w-11/12" style={{ minWidth: "100px" }}>
            {filteredFields.map((item: any, index: number) => {

              const elements = getContainerField(item, props.item)

              if (!elements) {
                return null
              }

              const { component } = elements
              if (containers.includes(component.type)) {
                return <ParentNode key={component.id} json={props.json} item={component} isCheckedIn={props.isCheckedIn} index={index} selectedElementId={selectedElementId} visibility={visibility} handleToggleVisibility={handleToggleVisibility} searchValue={props.searchValue} parentHoveredElId={component.parentId} scrollInfo={props?.scrollInfo} />
              }else{
                return <LeafNode key={component.id} item={component} selectedElementId={selectedElementId} isCheckedIn={props.isCheckedIn} index={index} json={props.json} searchValue={props.searchValue} parentHoveredElId={component.parentId} handleDelete={(event: React.MouseEvent<HTMLElement, MouseEvent>)=>handleDelete(event,component)} scrollInfo={props?.scrollInfo} ChildOffSetLeft={childWidth?.current?.offsetLeft}/>
              }
            })}
          </div>
        </div> :
        <div
          key={itemId}
          className={classNames(
            { "opacity-40": isDragging, "opacity-100": !isDragging },
            { "bg-highlight_onhover": hoveredElId === itemId }, 
            { "tree-drop-parent": isOverCurrent },

          )}
          ref={ref => isTopLevel ? drop(ref) : drag(drop(ref))}
        >
          <div className={classNames(
            "cursor-pointer flex items-center gap-2 justify-between",
            { "bg-highlight_onhover": hoveredElId === itemId }, 
            { "py-1": !isTopLevel }, 
            { "text-hdb-blue": !isTopLevel && selectedElementId === itemId && props.isCheckedIn })}>
            <div onClick={() => handleToggleVisibility(isTopLevel, itemId)}>
              {isChildrenVisibile ? <FolderOpenIcon /> : <FolderExpandIcon />}
            </div>
            <div className={`flex items-center gap-2 py-1 max-w-127 truncate`}><div onClick={(e) => {
              e.stopPropagation();
              setCondElVisibility(itemId, props.json.components)
              isTopLevel ? dispatch(setGridSelection({ isGridSelected: true })) : dispatch(setSelectedFormItemId({
                id: itemId,
                isAssetInstanceSelected: isAsset || isChildAsset,
              }))
            }}
            >
              {labelValue}
            </div></div>
            <div className="flex">
            {!newStyles.visible && !isTopLevel && <div className={`mr-2`}><CloseEye width={13} height={13}/></div>}
            { labelValue !="Canvas"  && hoveredElId === itemId  && <div className={`mr-2`}><DeleteIcon onClick={(e)=> handleDelete(e,props?.item)} width={13} height={13}/></div>}
            </div>
          </div>
          <div className="ml-8">
            {fields.map((item: any, index: number) => {

              const elements = getContainerField(item, props.item)

              if (!elements) {
                return null
              }

              const { component } = elements

              if (containers.includes(component.type)) {
                return <ParentNode key={component.id} json={props.json} item={component} isCheckedIn={props.isCheckedIn} index={index} selectedElementId={selectedElementId} visibility={visibility} handleToggleVisibility={handleToggleVisibility} />
              }
              return <LeafNode key={component.id} item={component} selectedElementId={selectedElementId} isCheckedIn={props.isCheckedIn} index={index} json={props.json} hoveredElId={itemId}  handleDelete={(event: React.MouseEvent<HTMLElement, MouseEvent>)=>handleDelete(event,component)}/>
            })}
          </div>
        </div>
      }
    </>

  )
}